import React, { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import {
    Plus, Trash2, X, ChevronRight,
    Volume2, Lightbulb, Package, Settings2, Monitor, Layers,
    Cpu, Wrench, Star, Zap, AlertCircle,
} from "lucide-react";
import { toast } from "sonner";
import { api, BASE } from "../../services/api";

const ICON_MAP = { Volume2, Lightbulb, Package, Settings2, Monitor, Layers, Cpu, Wrench, Star, Zap };

const ICON_OPTIONS = [
    { name: "Volume2",   label: "Zvuk"      },
    { name: "Lightbulb", label: "Svetlo"    },
    { name: "Package",   label: "Balík"     },
    { name: "Settings2", label: "Nastavenia"},
    { name: "Monitor",   label: "Monitor"   },
    { name: "Layers",    label: "Vrstvy"    },
    { name: "Cpu",       label: "Čip"       },
    { name: "Wrench",    label: "Kľúč"      },
    { name: "Star",      label: "Hviezda"   },
    { name: "Zap",       label: "Blesk"     },
];

const ACCENT_PRESETS = [
    "#0099FF", "#a855f7", "#22c55e", "#f59e0b",
    "#ec4899", "#f97316", "#14b8a6", "#f43f5e",
];

function resolveImg(url) {
    if (!url) return "";
    return url.startsWith("http") ? url : `${BASE}${url}`;
}

// ── Create modal ──────────────────────────────────────────────────────────────
function CreateModal({ onClose, onCreated }) {
    const [form, setForm] = useState({ title: "", short_desc: "", slug: "", icon_name: "Layers", accent: "#0099FF" });
    const [saving, setSaving] = useState(false);

    const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

    const autoSlug = (title) =>
        title.toLowerCase()
            .normalize("NFD").replace(/[̀-ͯ]/g, "")
            .replace(/[^a-z0-9]+/g, "-").replace(/(^-|-$)/g, "");

    const handleTitleChange = (v) => {
        set("title", v);
        set("slug", autoSlug(v));
    };

    const save = async () => {
        if (!form.title.trim() || !form.slug.trim()) { toast.error("Vyplňte názov"); return; }
        setSaving(true);
        try {
            const created = await api.createSluzba({ ...form, order: 99 });
            toast.success("Služba vytvorená");
            onCreated(created);
        } catch (e) {
            toast.error(e.message || "Chyba");
        } finally {
            setSaving(false);
        }
    };

    const PreviewIcon = ICON_MAP[form.icon_name] || Layers;

    return (
        <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 flex items-center justify-center p-4">
            <div className="absolute inset-0 bg-black/70 backdrop-blur-sm" onClick={onClose} />
            <motion.div
                initial={{ scale: 0.95, opacity: 0 }} animate={{ scale: 1, opacity: 1 }}
                exit={{ scale: 0.95, opacity: 0 }} transition={{ type: "spring", damping: 28, stiffness: 300 }}
                className="relative w-full max-w-lg bg-nova-surface border border-white/10 rounded-2xl overflow-hidden"
            >
                <div className="flex items-center justify-between px-6 py-4 border-b border-white/8">
                    <h2 className="font-heading font-black text-white">Nová služba</h2>
                    <button onClick={onClose} className="text-zinc-400 hover:text-white transition-colors">
                        <X className="w-5 h-5" />
                    </button>
                </div>

                <div className="px-6 py-6 space-y-5">
                    {/* Preview */}
                    <div className="flex items-center gap-3 p-3 rounded-xl border border-white/8"
                        style={{ background: `${form.accent}0d` }}>
                        <div className="w-10 h-10 rounded-lg flex items-center justify-center"
                            style={{ background: `${form.accent}14`, border: `1px solid ${form.accent}40` }}>
                            <PreviewIcon className="w-5 h-5" style={{ color: form.accent }} strokeWidth={1.6} />
                        </div>
                        <div>
                            <div className="font-heading font-bold text-white text-sm">{form.title || "Názov služby"}</div>
                            <div className="font-mono text-[10px] text-zinc-500">/sluzby/{form.slug || "slug"}</div>
                        </div>
                    </div>

                    <div>
                        <label className="font-mono text-[10px] uppercase tracking-wider text-zinc-500 mb-2 block">Názov *</label>
                        <input
                            value={form.title} onChange={e => handleTitleChange(e.target.value)} autoFocus
                            placeholder="napr. Pevné inštalácie"
                            className="w-full bg-nova-black/50 border border-white/8 rounded-xl px-4 py-3 text-white outline-none focus:border-nova-cyan/40 font-heading font-bold"
                        />
                    </div>

                    <div>
                        <label className="font-mono text-[10px] uppercase tracking-wider text-zinc-500 mb-2 block">Slug (URL) *</label>
                        <input
                            value={form.slug} onChange={e => set("slug", e.target.value)}
                            className="w-full bg-nova-black/50 border border-white/8 rounded-xl px-4 py-3 text-zinc-300 outline-none focus:border-nova-cyan/40 font-mono text-sm"
                        />
                    </div>

                    <div>
                        <label className="font-mono text-[10px] uppercase tracking-wider text-zinc-500 mb-2 block">Krátky popis</label>
                        <textarea
                            value={form.short_desc} onChange={e => set("short_desc", e.target.value)}
                            rows={2} placeholder="Stručný popis pre kartu…"
                            className="w-full bg-nova-black/50 border border-white/8 rounded-xl px-4 py-3 text-zinc-300 outline-none focus:border-nova-cyan/40 resize-none text-sm leading-relaxed"
                        />
                    </div>

                    <div>
                        <label className="font-mono text-[10px] uppercase tracking-wider text-zinc-500 mb-2 block">Ikona</label>
                        <div className="grid grid-cols-5 gap-2">
                            {ICON_OPTIONS.map(opt => {
                                const Ic = ICON_MAP[opt.name];
                                return (
                                    <button key={opt.name} type="button"
                                        onClick={() => set("icon_name", opt.name)}
                                        className={`flex flex-col items-center gap-1 p-2.5 rounded-lg border transition-all ${
                                            form.icon_name === opt.name
                                                ? "border-nova-cyan/60 bg-nova-cyan/10 text-nova-cyan"
                                                : "border-white/8 text-zinc-500 hover:border-white/20 hover:text-white"
                                        }`}>
                                        <Ic className="w-4 h-4" strokeWidth={1.6} />
                                        <span className="font-mono text-[9px]">{opt.label}</span>
                                    </button>
                                );
                            })}
                        </div>
                    </div>

                    <div>
                        <label className="font-mono text-[10px] uppercase tracking-wider text-zinc-500 mb-2 block">Farba akcentu</label>
                        <div className="flex items-center gap-2 flex-wrap">
                            {ACCENT_PRESETS.map(c => (
                                <button key={c} type="button" onClick={() => set("accent", c)}
                                    className={`w-7 h-7 rounded-full border-2 transition-all ${form.accent === c ? "border-white scale-110" : "border-transparent hover:scale-105"}`}
                                    style={{ background: c }} />
                            ))}
                            <input type="color" value={form.accent} onChange={e => set("accent", e.target.value)}
                                className="w-7 h-7 rounded-full cursor-pointer bg-transparent border-0 p-0" title="Vlastná farba" />
                        </div>
                    </div>
                </div>

                <div className="px-6 pb-6 flex justify-end gap-2">
                    <button onClick={onClose} className="px-4 py-2 rounded-lg text-zinc-400 hover:text-white text-sm transition-colors">
                        Zrušiť
                    </button>
                    <button onClick={save} disabled={saving}
                        className="flex items-center gap-2 px-5 py-2 rounded-lg bg-nova-cyan text-black font-semibold text-sm hover:bg-nova-cyan/90 disabled:opacity-50 transition-colors">
                        <Plus className="w-4 h-4" />
                        {saving ? "Vytváram…" : "Vytvoriť"}
                    </button>
                </div>
            </motion.div>
        </motion.div>
    );
}

// ── Main page ─────────────────────────────────────────────────────────────────
export default function AdminSluzby() {
    const navigate = useNavigate();
    const [services, setServices] = useState([]);
    const [loading, setLoading] = useState(true);
    const [creating, setCreating] = useState(false);
    const [deleting, setDeleting] = useState(null);

    const load = useCallback(async () => {
        setLoading(true);
        try {
            setServices(await api.getSluzby());
        } catch {
            toast.error("Nepodarilo sa načítať služby");
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => { load(); }, [load]);

    const handleCreated = (svc) => {
        setCreating(false);
        navigate(`/admin/sluzby/${svc.slug}`);
    };

    const handleDelete = async (slug, title) => {
        if (!window.confirm(`Zmazať „${title}"?`)) return;
        setDeleting(slug);
        try {
            await api.deleteSluzba(slug);
            setServices(prev => prev.filter(s => s.slug !== slug));
            toast.success("Služba zmazaná");
        } catch {
            toast.error("Chyba pri mazaní");
        } finally {
            setDeleting(null);
        }
    };

    if (loading) {
        return (
            <div className="space-y-3 animate-pulse">
                {[1, 2, 3, 4, 5, 6].map(n => (
                    <div key={n} className="h-24 bg-nova-surface/40 rounded-2xl" />
                ))}
            </div>
        );
    }

    return (
        <>
            <div className="flex items-center justify-between mb-8">
                <div>
                    <h1 className="font-heading text-2xl font-black text-white tracking-tight">Správa služieb</h1>
                    <p className="text-zinc-500 text-sm mt-1">
                        {services.length} {services.length === 1 ? "služba" : services.length < 5 ? "služby" : "služieb"}
                    </p>
                </div>
                <button
                    onClick={() => setCreating(true)}
                    className="flex items-center gap-2 px-4 py-2.5 rounded-xl bg-nova-cyan text-nova-black font-semibold text-sm hover:bg-nova-cyan/90 transition-colors"
                >
                    <Plus className="w-4 h-4" /> Nová služba
                </button>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-4">
                {services.map(svc => {
                    const Icon = ICON_MAP[svc.icon_name] || Layers;
                    const hasHero = !!svc.hero_image;
                    const photoCount = (svc.images || []).length;
                    const accent = svc.accent || "#0099ff";

                    return (
                        <motion.div
                            key={svc.slug}
                            whileHover={{ y: -2 }}
                            className="group relative overflow-hidden rounded-2xl border border-white/8 hover:border-white/20 transition-all bg-nova-surface/40 cursor-pointer"
                            onClick={() => navigate(`/admin/sluzby/${svc.slug}`)}
                        >
                            {/* Hero / accent header */}
                            {hasHero ? (
                                <div className="h-32 overflow-hidden relative">
                                    <img
                                        src={resolveImg(svc.hero_image)} alt={svc.title}
                                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                                    />
                                    <div className="absolute inset-0 bg-gradient-to-b from-transparent to-nova-black/80" />
                                </div>
                            ) : (
                                <div className="h-20 flex items-center justify-center relative overflow-hidden"
                                    style={{ background: `${accent}0d` }}>
                                    <div className="absolute inset-0 opacity-20"
                                        style={{ background: `radial-gradient(ellipse at center, ${accent}60, transparent 70%)` }} />
                                    <Icon className="w-8 h-8 relative z-10" style={{ color: accent }} strokeWidth={1.3} />
                                </div>
                            )}

                            <div className="p-4">
                                <div className="flex items-start justify-between gap-2">
                                    <div className="flex items-center gap-2.5 min-w-0">
                                        <div className="w-8 h-8 rounded-lg flex items-center justify-center shrink-0"
                                            style={{ background: svc.faint || `${accent}14`, border: `1px solid ${svc.border || accent + "33"}` }}>
                                            <Icon className="w-3.5 h-3.5" style={{ color: accent }} strokeWidth={1.6} />
                                        </div>
                                        <div className="min-w-0">
                                            <div className="font-heading font-bold text-white text-sm leading-tight truncate">{svc.title}</div>
                                            <div className="text-zinc-600 text-xs mt-0.5 truncate">{svc.tagline || svc.short_desc}</div>
                                        </div>
                                    </div>
                                    <div className="flex items-center gap-0.5 shrink-0">
                                        <div className="p-1.5 text-zinc-600 group-hover:text-white transition-colors">
                                            <ChevronRight className="w-4 h-4" />
                                        </div>
                                        <button
                                            onClick={e => { e.stopPropagation(); handleDelete(svc.slug, svc.title); }}
                                            disabled={deleting === svc.slug}
                                            className="p-1.5 text-zinc-700 hover:text-red-400 transition-colors disabled:opacity-40"
                                        >
                                            <Trash2 className="w-3.5 h-3.5" />
                                        </button>
                                    </div>
                                </div>

                                <div className="mt-3 flex items-center gap-3 text-[10px] font-mono text-zinc-600">
                                    <span>{(svc.features || []).length} funkcií</span>
                                    <span>·</span>
                                    <span>{(svc.useCases || []).length} využití</span>
                                    <span>·</span>
                                    <span className={photoCount > 0 ? "text-nova-cyan" : ""}>
                                        {photoCount} {photoCount === 1 ? "fotka" : photoCount < 5 ? "fotky" : "fotiek"}
                                    </span>
                                    {!hasHero && (
                                        <span className="flex items-center gap-1 text-amber-500/70 ml-auto">
                                            <AlertCircle className="w-3 h-3" /> bez fotky
                                        </span>
                                    )}
                                </div>
                            </div>
                        </motion.div>
                    );
                })}
            </div>

            <AnimatePresence>
                {creating && (
                    <CreateModal
                        key="create"
                        onClose={() => setCreating(false)}
                        onCreated={handleCreated}
                    />
                )}
            </AnimatePresence>
        </>
    );
}
